import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Typography,
    Chip,
    Tabs,
    Tab,
    Stack,
    Divider,
    LinearProgress,
} from "@mui/material";
import {
    Info as OverviewIcon,
    Settings as ConfigIcon,
    Star as FeaturesIcon,
    Security as SecurityIcon,
    CreditCard as BillingIcon,
    Business,
    Email,
    Language,
    Key,
    CloudQueue,
    Storage as StorageIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

import { clientApi } from "../../api/tenantApi"
import CustomCard from "../../components/common/CustomCard";
import CustomButton from "../../components/common/CustomButton";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CustomHeader from "../../components/common/CustomHeader";



// Default fallback values
const DEFAULT_CLOUD_CONFIG = {
    provider: "AWS S3",
    bucketName: "default-bucket",
    region: "us-east-1",
    accessKey: "default-access-key",
    secretKey: "default-secret",
};

const DEFAULT_DB_CONFIG = {
    httpHostname: "localhost",
    httpPort: "8082",
    grpcHostname: "localhost",
    grpcPort: "50051",
    database: "default_db",
    username: "admin",
};

// Small reusable info section
const InfoBlock = ({ icon: Icon, label, value, code = false }) => (
    <Box>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            {Icon && <Icon color="action" fontSize="small" />}
            <Typography variant="body2" fontWeight={600} color="text.secondary">
                {label}
            </Typography>
        </Stack>
        <Typography
            variant="body1"
            fontWeight={600}
            sx={
                code
                    ? {
                        backgroundColor: "#f1f5f9",
                        p: 1.5,
                        borderRadius: 1,
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                    }
                    : {}
            }
        >
            {value || "Not Available"}
        </Typography>
    </Box>
);

const ConfigSection = ({ title, icon: Icon, config, codeKeys = [] }) => (
    <CustomCard sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={3}>
            <Icon color="primary" />
            <Typography variant="h6" fontWeight={700}>
                {title}
            </Typography>
        </Stack>
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
            }}
        >
            {Object.entries(config).map(([key, value]) => (
                <InfoBlock
                    key={key}
                    label={key.replace(/([A-Z])/g, " $1")}
                    value={value}
                    code={codeKeys.includes(key)}
                />
            ))}
        </Box>
    </CustomCard>
);

const TokenUsageSection = ({ tokenUsage, tenantDetails, navigate }) => (
    <CustomCard sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight={700}>
                Token Usage Statistics
            </Typography>
            <CustomButton
                label="View Details"
                variant="text"
                onClick={() => navigate("/dash", { state: { tenantDetails } })}
            />
        </Stack>

        <Typography variant="body2" color="text.secondary" mb={3}>
            Total tokens consumed: {tokenUsage.total.toLocaleString()}
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 4 }}>
            <CustomCard sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Input Tokens
                </Typography>
                <Typography variant="h5" fontWeight={700} color="primary">
                    {tokenUsage.inputTokens.toLocaleString()}
                </Typography>
            </CustomCard>
            <CustomCard sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Output Tokens
                </Typography>
                <Typography variant="h5" fontWeight={700} color="secondary">
                    {tokenUsage.outputTokens.toLocaleString()}
                </Typography>
            </CustomCard>
        </Box>

        <Divider sx={{ mb: 3 }} />
        <Typography variant="h6" fontWeight={700} mb={2}>
            Token Distribution by Process
        </Typography>
        <Stack spacing={2}>
            {tokenUsage.breakdown.map((item) => (
                <Box key={item.process}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" fontWeight={600}>
                            {item.process}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                            {item.tokens?.toLocaleString()} ({item.percentage}%)
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={parseFloat(item.percentage)}
                        sx={{
                            height: 8,
                            borderRadius: 1,
                            backgroundColor: "#e5e7eb",
                            "& .MuiLinearProgress-bar": { backgroundColor: "#3b82f6" },
                        }}
                    />
                </Box>
            ))}
        </Stack>
    </CustomCard>
);

const TenantDashboardMUI = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const tenantDetails = location.state?.tenant;

    const [tenant, setTenant] = useState(null);
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "" });



    const tabData = [
        { label: "Account", icon: <OverviewIcon /> },
        { label: "Token Usage", icon: <ConfigIcon /> },
        { label: "Cloud Config.", icon: <FeaturesIcon /> },
        { label: "DB Config", icon: <SecurityIcon /> },
        { label: "Plan", icon: <BillingIcon /> },
    ];

    const handleChange = (_, newValue) => setTab(newValue);

    const getConfigWithDefaults = (config, defaults) => {
        if (!config) return defaults;
        return Object.fromEntries(
            Object.entries(defaults).map(([k, v]) => [k, config[k] || v])
        );
    };
    const showSnackbar = (message, severity = "info") => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const fetchTenant = async () => {
        try {
            setLoading(true);

            const response = await clientApi.getTenantDetails(tenantDetails.id);
            // console.log("GetTenantDetails response:", response);

            const { status, message, data } = response || {};
            //1️⃣ Network error
            if (status === 0) {
                showSnackbar(
                    "Network error. Please check your internet connection.",
                    "warning"
                );
                setTenant(null);
                return;
            }
            // 2️⃣ Client errors (400)
            if (status === 400) {
                showSnackbar(message || "Invalid request.", "warning");
                setTenant(null);
                return;
            }


            // 3️⃣ Not found (404)
            if (status === 404) {
                showSnackbar(message || "Tenant data not found.", "info");
                setTenant(null);
                return;
            }


            // 4️⃣ Server errors (500+)
            if (status >= 500) {
                showSnackbar(
                    message || "Server error while fetching tenant details.",
                    "error"
                );
                setTenant(null);
                return;
            }



            // 5️⃣ Success (200)
            if (status === 200 && data) {
                const tokenData = Array.isArray(data.tokenData)
                    ? data.tokenData
                    : [];

                const totalTokens = tokenData.reduce(
                    (sum, t) => sum + (t?.total_tokens || 0),
                    0
                );

                setTenant({
                    companyName: data.tenantData?.companyName ?? "",
                    companyDomain: data.tenantData?.companyDomain ?? "",
                    email: data.tenantData?.email ?? "",
                    plan: data.tenantData?.plan ?? "",

                    tokenUsage: {
                        total: totalTokens,
                        inputTokens: tokenData.reduce(
                            (sum, t) => sum + (t?.input_tokens || 0),
                            0
                        ),
                        outputTokens: tokenData.reduce(
                            (sum, t) => sum + (t?.output_tokens || 0),
                            0
                        ),
                        breakdown: tokenData.map((t) => ({
                            process: t?.process || "Unknown",
                            tokens: t?.total_tokens || 0,
                            percentage: totalTokens
                                ? ((t.total_tokens * 100) / totalTokens).toFixed(1)
                                : 0,
                        })),
                    },

                    cloudConfig: getConfigWithDefaults(
                        data.cloudConfig,
                        DEFAULT_CLOUD_CONFIG
                    ),

                    dbConfig: getConfigWithDefaults(
                        data.dbconfig,
                        DEFAULT_DB_CONFIG
                    ),
                });

                // Optional success message
                // showSnackbar(message, "success");
                return;
            }


            // 6️⃣ Fallback (unexpected)
            showSnackbar("Unexpected response from server.", "error");
            setTenant(null);



        } catch (err) {
            console.error("Failed to fetch tenant data:", err);
            showSnackbar(
                "Something went wrong while loading tenant details.",
                "error"
            );
            setTenant(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTenant();
    }, []);


    return (
        <>
            {loading ? (
                <SkeletonLoader type="tenant-details" />
            ) : (
                <Box sx={{ backgroundColor: "#f4f5f7", minHeight: "100vh" }}>
                    {/* Main Heading */}

                    <CustomHeader title="Tenant Details" />


                    <CustomCard sx={{ p: 4, m: 1 }}>
                        {/* HEADER */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                            <Box>
                                <Typography variant="h5" fontWeight={700}>
                                    {tenant.companyName}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {tenant.email}
                                </Typography>
                            </Box>
                            <Chip label="Active" color="primary" sx={{ fontWeight: 700 }} />
                        </Stack>

                        <Divider sx={{ mb: 3 }} />

                        {/* TABS */}
                        <Tabs
                            value={tab}
                            onChange={handleChange}
                            variant="fullWidth"
                            sx={{
                                mb: 3,
                                backgroundColor: "#f3f4f6",
                                borderRadius: 2,
                                "& .MuiTabs-indicator": { display: "none" },
                            }}
                        >
                            {tabData.map((item, index) => (
                                <Tab
                                    key={item.label}
                                    icon={item.icon}
                                    iconPosition="start"
                                    label={item.label}
                                    sx={{
                                        textTransform: "none",
                                        fontSize: "0.85rem",
                                        fontWeight: 600,
                                        color: tab === index ? "#f43f5e" : "#545657",
                                        "&.Mui-selected": {
                                            backgroundColor: "#fff",
                                            borderRadius: 1,
                                            color: "#161616",
                                        },
                                    }}
                                />
                            ))}
                        </Tabs>

                        {/* TAB CONTENT */}
                        {tab === 0 && (
                            <CustomCard sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    Account Details
                                </Typography>
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                                    <Stack spacing={2}>
                                        <InfoBlock icon={Business} label="Company Name" value={tenant.companyName} />
                                        <InfoBlock icon={Language} label="Company Domain" value={tenant.companyDomain} />
                                        <InfoBlock icon={Email} label="Company Email" value={tenant.email} />
                                    </Stack>
                                    <Stack spacing={2}>
                                        <InfoBlock icon={Key} label="Gemini API Key" value={tenant.geminiApiKey} code />
                                    </Stack>
                                </Box>
                            </CustomCard>
                        )}

                        {tab === 1 && (
                            <TokenUsageSection tokenUsage={tenant.tokenUsage} tenantDetails={tenantDetails} navigate={navigate} />
                        )}

                        {tab === 2 && <ConfigSection title="Cloud Configuration" icon={CloudQueue} config={tenant.cloudConfig} codeKeys={["accessKey", "secretKey"]} />}
                        {tab === 3 && <ConfigSection title="Database Configuration" icon={StorageIcon} config={tenant.dbConfig} codeKeys={Object.keys(tenant.dbConfig)} />}
                        {tab === 4 && (
                            <CustomCard sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight={700} mb={1}>
                                    Subscription Plan
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={3}>
                                    Current plan and billing details
                                </Typography>
                                <CustomCard sx={{ backgroundColor: "#f8fafc", p: 3 }}>
                                    <Typography variant="h4" fontWeight={700} color="primary">
                                        {tenant.plan}
                                    </Typography>
                                </CustomCard>
                            </CustomCard>
                        )}
                    </CustomCard>

                    {/* SNACKBAR */}
                    <CustomSnackbar
                        open={snackbar.open}
                        message={snackbar.message}
                        severity={snackbar.severity}
                        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    />
                </Box>)}
        </>
    );
};

export default TenantDashboardMUI;
